import React, { FC, ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import { AssignedTo, FileDto, FolderDto, UserRole } from "@green0meter/api-client";

import CreateEditFolderModal from "../CreateEditFolderModal";
import CreateEditDocumentModal from "../CreateEditDocumentModal";

import { IconButton, Stack, Tooltip, Typography } from "@components/base";
import { Chip, ConfirmationModal, Loader, Table } from "@components/common";
import { useUserStore } from "@store/user";
import {
  useCreateFolder,
  useDeleteFileFromFolder,
  useDeleteFolder,
  useGetFolder,
  useUpdateFile,
  useUpdateFolder,
} from "@api";
import { formatDateUtc, formatFileSize, getFileTypeIcon } from "@helpers";
import { TableColumn } from "@components/common/Table";
import { DOCUMENT_TYPE_OPTIONS_MAP, EDITABLE_FILE_EXTENSIONS } from "@type/File";
import { ASSIGN_TO_VALUES, canUserManageFiles } from "@type/User";

// TODO: refactor this component into smaller bits
const isFolder = (record: FileDto | FolderDto): record is FolderDto => {
  return "children" in record;
};

export const isFileEditable = (fileName: string): boolean => {
  const ext = fileName.split(".").pop()?.toUpperCase();

  if (!ext) return false;

  return !Object.values(EDITABLE_FILE_EXTENSIONS).some((extensions) =>
    extensions.includes(ext),
  );
};

const FileTable: FC<{ folderId: string; setFolderId: (id: string) => void }> = ({
  folderId,
  setFolderId,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { folder, isFetchingGetFolder } = useGetFolder(user, folderId);
  const { folder: rootFolder } = useGetFolder(user);
  const { updateFile, isPendingUpdateFile } = useUpdateFile(user);
  const { updateFolder } = useUpdateFolder(user);
  const { deleteFileFromFolderAsync } = useDeleteFileFromFolder(user);
  const { deleteFolderAsync } = useDeleteFolder(user);
  const [selectedRecord, setSelectedRecord] = useState<FileDto | FolderDto | null>(
    null,
  );

  const assignedTos = Object.entries(ASSIGN_TO_VALUES);

  const [confirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [editFileModalOpen, setIsEditFileModalOpen] = useState<boolean>(false);
  const [editFolderModalOpen, setIsEditFolderModalOpen] = useState<boolean>(false);
  const [createFolderModalOpen, setIsCreateFolderModalOpen] =
    useState<boolean>(false);
  const [duplicateNameModalOpen, setDuplicateNameModalOpen] =
    useState<boolean>(false);
  const { createFolder, isPendingCreateFolder } = useCreateFolder(user);
  const confirmReplaceRef = useRef<((result: boolean) => void) | null>(null);

  const navigate = useNavigate();

  const handleOpenDetail = (fileId: string): void => {
    navigate(`/document-management-hub/${fileId}`);
  };

  const handleDeleteRow = async (record: FolderDto | FileDto): Promise<void> => {
    if (isFolder(record)) {
      deleteFolderAsync(record.id);
    } else {
      deleteFileFromFolderAsync(record.id);
    }
  };

  const handleCreateFolder = async (
    folderName: string,
    userRole: AssignedTo,
    parentFolderId: string,
    userIds: string[] = [],
  ): Promise<void> => {
    await createFolder({
      name: folderName,
      parentId: parentFolderId,
      assignedTo: userRole,
      userIds,
    }).then(() => {
      setIsCreateFolderModalOpen(false);
    });
  };

  // TODO add folder name to table?

  const columns: TableColumn<FileDto | FolderDto>[] = [
    {
      key: "type",
      renderCellContent: (record) => getFileTypeIcon(record.name),
      tableCellProps: { sx: { maxWidth: 50 } },
      title: t("documentHub.file.table.fileType"),
    },
    {
      dataKey: "name",
      key: "name",
      title: t("documentHub.file.table.fileName"),
    },
    {
      // dataKey: "createdAt",
      key: "createdAt",
      title: t("documentHub.file.table.createdAt"),
      renderCellContent: (record) => formatDateUtc(record?.createdAt),
    },
    {
      // dataKey: "connection",
      key: "connection",
      title: t("documentHub.file.table.connection"),
      renderCellContent: () => "-",
    },
    {
      // dataKey: "datapoints",
      key: "datapoints",
      title: t("documentHub.file.table.linkedDatapoint"),
      renderCellContent: () => "-",
    },
    {
      // dataKey: "createdBy",
      key: "createdBy",
      title: t("documentHub.file.table.fileOwner"),
      renderCellContent: (record) =>
        record?.createdBy
          ? `${record.createdBy.firstName} ${record.createdBy.lastName}`
          : "-",
    },
    {
      // dataKey: "size",
      key: "size",
      title: t("documentHub.file.table.fileSize"),
      renderCellContent: (record) =>
        record?.size
          ? formatFileSize(record?.size)
          : t("documentHub.file.table.empty"),
    },
    {
      key: "assignedTo",
      title: t("documentHub.file.table.permissions"),
      renderCellContent: (record): ReactElement => {
        const isMixed =
          Array.isArray(record.assignment?.userIds) &&
          record.assignment.userIds.length > 0;

        return (
          <Chip
            label={
              isMixed
                ? t(`assignTo.${assignedTos[AssignedTo.Mixed][1]}`)
                : t(`assignTo.${assignedTos[record.assignment?.assignedTo ?? 0][1]}`)
            }
            type={isMixed ? AssignedTo.Mixed : Number(record.assignment?.assignedTo)}
            size="small"
          />
        );
      },
    },
    {
      key: "actions",
      title: t("common.actions"),
      tableCellProps: {
        align: "right",
      },
      renderCellContent: (record) => (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Tooltip title={t("common.edit")} placement="top">
            <IconButton
              size="small"
              disabled={
                !canUserManageFiles(
                  user?.role ?? UserRole.Viewer,
                  record.assignment?.assignedTo ?? AssignedTo.Everyone,
                  user?.id ?? "",
                  Array.isArray(record.assignment?.userIds)
                    ? record.assignment.userIds
                    : [],
                  record.createdBy?.id,
                ) ||
                (!isFolder(record) && isFileEditable(record.name))
              }
              onClick={() => {
                setSelectedRecord(record);
                if (isFolder(record)) {
                  setIsEditFolderModalOpen(true);
                } else {
                  setIsEditFileModalOpen(true);
                }
              }}
              color="primary"
            >
              <FeatherIcon icon="edit-3" size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")} placement="top">
            <IconButton
              size="small"
              disabled={
                !canUserManageFiles(
                  user?.role ?? UserRole.Viewer,
                  record.assignment?.assignedTo ?? AssignedTo.Everyone,
                  user?.id ?? "",
                  Array.isArray(record.assignment?.userIds)
                    ? record.assignment.userIds
                    : [],
                  record.createdBy?.id,
                )
              }
              onClick={() => {
                setSelectedRecord(record);
                setIsConfirmationModalOpen(true);
              }}
              color="error"
            >
              <FeatherIcon icon="trash-2" size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (!folder) return <Loader />;

  const handleCloseModal = (): void => {
    setSelectedRecord(null);
    setIsConfirmationModalOpen(false);
  };

  const handleConfirmModal = (): void => {
    if (!selectedRecord) return;
    setSelectedRecord(null);
    setIsConfirmationModalOpen(false);
    handleDeleteRow(selectedRecord);
  };

  const showReplaceConfirmModal = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setDuplicateNameModalOpen(true);
      confirmReplaceRef.current = resolve;
    });
  };

  const handleConfirmReplaceModal = (): void => {
    setDuplicateNameModalOpen(false);
    confirmReplaceRef.current?.(true);
    confirmReplaceRef.current = null;
  };

  const handleCloseNameDuplicityModal = (): void => {
    setDuplicateNameModalOpen(false);
    confirmReplaceRef.current?.(false);
    confirmReplaceRef.current = null;
  };

  const handleConfirmUpdateFileModal = async (
    fileId: string,
    fileName: string,
    parentFolderId: string,
    assignedTo: AssignedTo,
    userIds: string[] = [],
    replace: boolean = false,
  ): Promise<void> => {
    const targetFolder =
      parentFolderId === folder?.id
        ? folder
        : rootFolder?.children.find((f) => f.id === parentFolderId);

    const duplicateFileNameExists = targetFolder?.files?.find(
      (file) =>
        file.name.trim().toLowerCase() === fileName.trim().toLowerCase() &&
        file.id !== fileId,
    );

    if (duplicateFileNameExists && !replace) {
      const shouldReplace = await showReplaceConfirmModal();

      if (shouldReplace) {
        await handleConfirmUpdateFileModal(
          fileId,
          fileName,
          parentFolderId,
          assignedTo,
          userIds,
          true,
        );
      }

      return;
    }

    updateFile({
      fileId,
      dto: { fileName, parentFolderId, assignedTo, userIds, replace },
    });
    setIsEditFileModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmUpdateFolderModal = async (
    folderId: string,
    folderName: string,
    parentFolderId: string,
    assignedTo: AssignedTo,
    userIds: string[] = [],
  ): Promise<void> => {
    updateFolder({
      folderId,
      dto: {
        name: folderName,
        parentFolderId: parentFolderId,
        assignedTo,
        userIds,
      },
    });
    setIsEditFolderModalOpen(false);
    setSelectedRecord(null);
  };

  return (
    <>
      <Stack gap={4}>
        <Typography textAlign="left" variant="sectionLabel">
          {t("documentHub.filesSectionTitle")}
        </Typography>
        <Table<FileDto | FolderDto>
          columns={columns}
          data={
            folder?.files || folder?.children
              ? [...folder.children, ...folder.files]
              : []
          }
          onRowClick={(record) =>
            isFileEditable(record.name)
              ? setFolderId(record.id)
              : handleOpenDetail(record.id)
          }
          dataPlaceholder={
            isFetchingGetFolder ? (
              <Loader />
            ) : (
              <Typography textAlign="center">
                {t("documentHub.noFilesFound")}
              </Typography>
            )
          }
          onDropRow={(dragged, droppedOn) => {
            if (isFolder(dragged)) {
              handleConfirmUpdateFolderModal(
                dragged.id,
                dragged.name,
                droppedOn.id,
                droppedOn.assignment?.assignedTo || AssignedTo.OnlyOwner,
              );
            } else {
              handleConfirmUpdateFileModal(
                dragged.id,
                dragged.name,
                droppedOn.id,
                droppedOn.assignment?.assignedTo || AssignedTo.OnlyOwner,
              );
            }
          }}
          isDropAllowed={(dragged, droppedOn) => {
            if (!isFolder(droppedOn)) return false;
            if (dragged.id === droppedOn.id) return false;

            return true;
          }}
          // pagination={pagination}
        />
      </Stack>
      <ConfirmationModal
        key={"confirm-action-modal"}
        open={confirmationModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmModal}
        title={t("documentHub.confirmModal.title")}
        description={t("documentHub.confirmModal.description")}
        cancelText={t("common.cancel")}
        confirmText={t("common.confirm")}
      />
      <ConfirmationModal
        key={"confirm-file-name-duplicity"}
        open={duplicateNameModalOpen}
        onClose={handleCloseNameDuplicityModal}
        onConfirm={handleConfirmReplaceModal}
        title={t("documentHub.duplicitFileName.title")}
        description={t("documentHub.duplicitFileName.description", {
          fileName: selectedRecord?.name,
        })}
        cancelText={t("common.cancel")}
        confirmText={t("common.confirm")}
      />
      {selectedRecord && (
        <CreateEditDocumentModal
          open={editFileModalOpen}
          onClose={() => {
            setIsEditFileModalOpen(false);
            setSelectedRecord(null);
          }}
          onConfirmEdit={(
            documentId,
            documentName,
            documentType,
            assignedTo,
            folderId,
            userIds,
          ) => {
            handleConfirmUpdateFileModal(
              documentId,
              documentName,
              folderId,
              assignedTo,
              userIds,
            );
          }}
          folder={folder}
          mode="edit"
          documentMeta={selectedRecord as FileDto}
          documentTypes={Object.values(DOCUMENT_TYPE_OPTIONS_MAP)}
          isLoading={isPendingUpdateFile}
          rootFolder={rootFolder as FolderDto}
          onCreateFolderClick={() => setIsCreateFolderModalOpen(true)}
        />
      )}
      {editFolderModalOpen && (
        <CreateEditFolderModal
          key={"edit-folder-modal"}
          open={editFolderModalOpen}
          onClose={() => {
            setIsEditFolderModalOpen(false);
            setSelectedRecord(null);
          }}
          onConfirmEdit={(
            folderId,
            folderName,
            assignedTo,
            parentFolderId,
            userIds,
          ) => {
            handleConfirmUpdateFolderModal(
              folderId,
              folderName,
              parentFolderId,
              assignedTo,
              userIds,
            );
          }}
          isLoading={isPendingCreateFolder}
          folder={selectedRecord as FolderDto}
          rootFolder={rootFolder as FolderDto}
          mode="edit"
        />
      )}
      {createFolderModalOpen && (
        <CreateEditFolderModal
          key={"create-fodler-modal"}
          open={createFolderModalOpen}
          onClose={() => {
            setIsCreateFolderModalOpen(false);
            setSelectedRecord(null);
          }}
          onConfirm={(folderName, userRole, folderId, userIds) => {
            handleCreateFolder(folderName, userRole, folderId, userIds);
          }}
          rootFolder={rootFolder as FolderDto}
          isLoading={isPendingCreateFolder}
          folder={folder}
        />
      )}
    </>
  );
};

export default FileTable;
